'use client'

import { SpeedInsights } from '@vercel/speed-insights/react'
import { setDefaultOptions } from 'date-fns'
import { enUS, ja } from 'date-fns/locale'
import Gleap from 'gleap'
import { SessionProvider } from 'next-auth/react'
import dynamic from 'next/dynamic'
import { useCallback, useEffect, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import { Provider } from 'urql'

import configuration from '~/configuration'
import { SESSION_VERSION } from '~/core/constants/cookies'
import { AGENCY_TENANT, GLEAP_EVENTS } from '~/core/constants/enum'
import { DEFAULT_LOCALE, JAPAN_LOCALE, NODE_ENV, PUBLIC_APP_URL } from '~/core/constants/env'
import { useGraphQLRequest } from '~/core/hooks/use-graphQL'
import { Toast as ToastWrapper } from '~/core/ui/Toast'

import { useSubmitSignOutCHUB } from '~/lib/features/career-hub/hook/use-submit-sign-out'
import useNotificationStore from '~/lib/features/notifications/store'
import { useSubmitSignOut } from '~/lib/features/sign-out/hooks/use-submit-sign-out'
import { useRouterContext } from '~/lib/next/use-router-context'
import useBoundStore from '~/lib/store'
import useLoadingBlockStore from '~/lib/store/loading-block'
import useToastStore from '~/lib/store/toast'

import type { Session } from 'next-auth'
import GoogleTracking from '~/components/GoogleTracking'
import IdleTimerSettingWrapper from '~/components/IdleTimerSetting'
import NoSupportMobileDetectView from '~/components/Layout/NoSupportMobileDetectView'
import { NavigationPendingProvider, RouteLoadingOverlay } from '~/components/NavigationPending'
import NetworkWatcher from '~/components/NetworkWatcher'
import NotificationDrawerContainer from '~/components/Notifications/NotificationDrawerContainer'
import PermissionSettingWrapper from '~/components/PermissionSetting'
import { SearchProvider } from '~/components/UniversalSearch/USearchContext'
import type { IUserInformation } from '~/core/@types/global'

const AppRouteLoadingIndicator = dynamic(() => import('~/core/ui/AppRouteLoadingIndicator'), {
  ssr: false
})

const AppRouteLoadingBlockApp = dynamic(() => import('~/core/ui/AppRouteLoadingBlockApp'), {
  ssr: false
})

interface AuthProviderProps {
  children: React.ReactNode
  initialUser: IUserInformation | null
  initialSession: Session | null
}

const ClearCacheData = ({ isCHUB }: { isCHUB?: boolean }) => {
  // Function to clear complete cache data
  const { handleSignOut } = useSubmitSignOut()
  const { handleSignOut: handleSignOutCHUB } = useSubmitSignOutCHUB()

  const signOut = useCallback(async () => {
    await handleSignOut()
    if (Gleap && Gleap.getInstance().initialized === true) {
      Gleap.clearIdentity()
    }
  }, [])

  const signOutCHUB = useCallback(async () => {
    await handleSignOutCHUB()
    if (Gleap && Gleap.getInstance().initialized === true) {
      Gleap.clearIdentity()
    }
  }, [])

  const clearCache = () => {
    if (window) {
      const version = SESSION_VERSION
      const isCustomDomain =
        NODE_ENV === 'development'
          ? !['localhost:3000'].includes(window.location.host)
          : !PUBLIC_APP_URL?.includes(window.location.host)

      const storageVersion = localStorage.getItem('version') || null
      if (!isCustomDomain && storageVersion && storageVersion !== version) {
        if (isCHUB) {
          signOutCHUB()
        } else {
          signOut()
        }

        setTimeout(() => {
          localStorage.setItem('version', version)
        }, 100)
      } else if (!storageVersion) {
        localStorage.setItem('version', version)
      }
    }
  }

  useEffect(() => {
    clearCache()
  }, [])

  return null
}

function AppProvider({ initialUser, initialSession, children }: AuthProviderProps) {
  const { pathnameWithoutLocale, asPathWithoutLocale, locale } = useRouterContext()
  const { setUser } = useBoundStore()
  const { configToast, setToastClose, clearToast } = useToastStore()
  const { showLockApp, titleLockApp } = useLoadingBlockStore()
  const { openNotificationDrawer } = useNotificationStore()

  const [firstLoad, setFirstLoad] = useState(true)
  const [isAppReady, setIsAppReady] = useState(true)

  const isNotCareerPage = asPathWithoutLocale?.search('/careers/') === -1
  const isCareerHubPath = asPathWithoutLocale?.search('/careerhub/') > -1

  const client = useGraphQLRequest({
    isCareerHub: isCareerHubPath,
    language: isNotCareerPage ? initialUser?.language : locale
  })

  useEffect(() => {
    if (Gleap.getInstance().initialized === true && initialUser && firstLoad) {
      setFirstLoad(false)
      if (initialUser?.currentTenant?.interacted) {
        Gleap.trackEvent(GLEAP_EVENTS.firstJobCreated)
        Gleap.trackEvent(GLEAP_EVENTS.firstCandidateAdded)
        Gleap.trackEvent(GLEAP_EVENTS.firstMemberInvited)
        if (initialUser?.currentTenant?.companyKind !== AGENCY_TENANT) {
          Gleap.trackEvent(GLEAP_EVENTS.firstLocationCreated)
          Gleap.trackEvent(GLEAP_EVENTS.firstDepartmentCreated)
        }
      }
    }
  }, [Gleap, initialUser])

  useEffect(() => {
    const planName = initialUser?.currentTenant?.tenantPlan?.name

    if (
      configuration.key.gleapApiKey &&
      Gleap &&
      Gleap.getInstance().initialized === false &&
      initialUser?.id &&
      initialUser?.currentTenant?.id &&
      planName &&
      isNotCareerPage
    ) {
      Gleap.setLanguage(initialUser?.language || DEFAULT_LOCALE)
      Gleap.initialize(configuration.key.gleapApiKey)
      Gleap.identify(initialUser?.id, {
        name: initialUser?.fullName,
        email: initialUser?.email,
        companyId: initialUser?.currentTenant.id,
        // @ts-ignore - doesn't need to fix
        companyName: initialUser?.currentTenant?.name,
        plan: planName,
        customData: {
          type: initialUser?.ownTenant
            ? `${initialUser?.currentTenant.companyKind}-owner`
            : `${initialUser?.currentTenant.companyKind}`,
          companyId: initialUser?.currentTenant.id,
          companyName: initialUser?.currentTenant?.name
        }
      })
    }
  }, [initialUser])

  // Update current user in store and date-fns locale
  useEffect(() => {
    if (initialUser) {
      setUser(initialUser)
    }

    if (isNotCareerPage) {
      setDefaultOptions({ locale: initialUser?.language === JAPAN_LOCALE ? ja : enUS })
    } else {
      setDefaultOptions({ locale: locale === JAPAN_LOCALE ? ja : enUS })
    }
    // after setting user info, hide loading,
    if (isAppReady) {
      setIsAppReady(false)
    }
  }, [isAppReady, initialUser])

  return (
    <Provider value={client}>
      <SessionProvider session={initialSession}>
        <NavigationPendingProvider>
          <NetworkWatcher />
          {isNotCareerPage === false ? (
            <GoogleTracking />
          ) : (
            <ClearCacheData isCHUB={isCareerHubPath} />
          )}
          <AppRouteLoadingIndicator />
          <RouteLoadingOverlay LoadingComponent={AppRouteLoadingBlockApp} />
          <PermissionSettingWrapper>
            <NoSupportMobileDetectView>
              {!isAppReady && children}
              <SearchProvider />
            </NoSupportMobileDetectView>
          </PermissionSettingWrapper>
          {/* Idle Timer SSO */}
          <IdleTimerSettingWrapper />
          {isNotCareerPage && openNotificationDrawer && <NotificationDrawerContainer />}
          <Toaster position="bottom-right" />
          <ToastWrapper
            {...configToast}
            setOpen={() => {
              setToastClose()
              setTimeout(clearToast, 100)
            }}
          />
          {showLockApp && <AppRouteLoadingBlockApp title={titleLockApp} />}
          {configuration.production && <SpeedInsights route={pathnameWithoutLocale} />}
        </NavigationPendingProvider>
      </SessionProvider>
    </Provider>
  )
}

export default AppProvider
